import {
  Address,
  ExtensionField,
  ExtensionFieldValue,
  LookupField,
  LookupFieldValue,
} from "types/payable";

export function setTimeToZero(date: Date) {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

function padNumberWithZeros(number: number, totalDigits: number) {
  // Given number=4 and totalDigits=2, it returns "04" etc.
  const numberStr = number.toString();
  return numberStr.padStart(totalDigits, "0");
}

// Extract time from the date object in the format "HH:mm".
export function getFormattedTime(date: Date) {
  return `${padNumberWithZeros(date.getHours(), 2)}:${padNumberWithZeros(
    date.getMinutes(),
    2
  )}`;
}

function normalizeAddress(address: Partial<Address>): Address {
  return {
    streetAddress: address.streetAddress ?? "",
    locality: address.locality ?? "",
    region: address.region ?? "",
    postalCode: address.postalCode ?? "",
    country: address.country ?? "",
  };
}

export function getDefaultValues(
  fields: LookupField[] | ExtensionField[],
  fieldValues?: Record<string, ExtensionFieldValue | LookupFieldValue>
) {
  return (fields as any[]).reduce(
    (defaultValues, field: LookupField | ExtensionField) => {
      if (field.type === "Address") {
        console.error("Address field is not currently supported.");

        // TODO: implement getting default values for Address field.
        // const fieldValue = fieldValues?.[field.name];
        // const address =
        //   typeof fieldValue?.value === "object" && !(fieldValue instanceof Date)
        //     ? (fieldValue as Address)
        //     : {};

        // const normalizedAddress = normalizeAddress(address);
        // defaultValues[field.name] = normalizedAddress;
      } else if (
        field.type === "NonblankText" ||
        field.type === "Integer" ||
        field.type === "Email" ||
        field.type === "PhoneNumber" ||
        field.type === "SelectOne" ||
        field.type === "DollarAmount"
      ) {
        defaultValues[field.name] = fieldValues?.[field.name]?.value ?? "";
      } else if (field.type === "Date") {
        defaultValues[field.name] = fieldValues?.[field.name]?.value;
      } else if (field.type === "DateTime") {
        const value = fieldValues?.[field.name]?.value;
        const date = value ? new Date(value) : undefined;

        defaultValues[field.name] = {
          date: date ? setTimeToZero(date) : undefined,
          time: date ? getFormattedTime(date) : undefined,
        };
      } else if (field.type === "SelectMany") {
        defaultValues[field.name] = fieldValues?.[field.name]?.value ?? [];
      } else if (field.type === "Boolean") {
        defaultValues[field.name] =
          fieldValues?.[field.name]?.value ?? undefined;
      } else {
        // Exhaustive check to make sure we handle all field types.
        throw field.type satisfies never;
      }

      return defaultValues;
    },
    {}
  );
}

const shouldIncludeField = (field: ExtensionField, fieldValue: any) => {
  if (fieldValue === undefined) {
    return false;
  }

  // Don't include the following fields in the payload if they're empty strings.
  // PhoneNumber and Email fields will fail validation on the API end if they're
  // passed as empty strings.
  const excludeEmptyStringFields = [
    "NonblankText",
    "Integer",
    "PhoneNumber",
    "Email",
    "SelectOne",
    "DollarAmount",
  ];

  return !(excludeEmptyStringFields.includes(field.type) && fieldValue === "");
};

const transformFieldValue = (field: ExtensionField, fieldValue: any) => {
  if (field.type === "DateTime") {
    return transformDateTimeField(fieldValue);
  }

  return {
    value: fieldValue,
    type: field.type,
  };
};

const transformDateTimeField = (fieldValue: any) => {
  if (!fieldValue.date || !fieldValue.time) {
    return null;
  }

  const [hours, minutes] = fieldValue.time.split(":").map(Number);
  const combinedDate = new Date(fieldValue.date);
  combinedDate.setHours(hours, minutes);

  return {
    value: combinedDate.toISOString(),
    type: "DateTime",
  } as ExtensionFieldValue;
};

export function prepareExtensionFieldsPayload(
  extensionFields: ExtensionField[] | undefined,
  formValues: any
) {
  if (!extensionFields) {
    return {};
  }

  const payload = extensionFields.reduce(
    (payload: Record<string, ExtensionFieldValue>, field) => {
      const fieldValue = formValues[field.name];

      if (!shouldIncludeField(field, fieldValue)) {
        return payload;
      }

      const transformedValue = transformFieldValue(field, fieldValue);
      if (transformedValue) {
        payload[field.name] = transformedValue;
      }

      return payload;
    },
    {}
  );

  return payload;
}
